import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function StatisticsView({ onBack }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [tooltip, setTooltip] = useState(null);
  const [teamsList, setTeamsList] = useState([]);

  useEffect(() => {
    fetchStats();
  }, [days, selectedTeam]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_URL}/api/stats/advanced?days=${days}${selectedTeam ? `&team=${encodeURIComponent(selectedTeam)}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        if (data.teams && teamsList.length === 0) {
          setTeamsList(data.teams.map(t => t.name).sort((a, b) => a.localeCompare(b, 'fr')));
        }
      } else {
        setError('Erreur lors du chargement des statistiques');
      }
    } catch (e) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '-';
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    if (hours < 24) return `${Math.round(hours)}h`;
    return `${Math.round(hours / 24)}j`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const actionTypeLabels = {
    'odoo_reply': 'Réponse Odoo',
    'odoo_note': 'Note interne',
    'odoo_status_update': 'Changement statut',
    'odoo_partner_update': 'Modification client',
    'odoo_assign_user': 'Assignation utilisateur',
    'odoo_assign_team': 'Assignation équipe',
    'odoo_spam': 'Spam',
    'odoo_delete': 'Suppression',
    'odoo_reopen': 'Réouverture',
    'whatsapp_send': 'Message WhatsApp',
    'whatsapp_ticket_status': 'Statut WhatsApp',
    'whatsapp_ticket_assign_user': 'Assignation WA',
    'whatsapp_ticket_assign_team': 'Équipe WA',
    'whatsapp_ticket_priority': 'Priorité WA',
    'whatsapp_link_partner': 'Liaison client WA',
    'whatsapp_ticket_reopen': 'Réouverture WA'
  };

  // --- Composants réutilisables ---

  const StatCard = ({ title, value, subtitle, color, icon }) => (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      padding: '20px',
      borderTop: `3px solid ${color}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '2rem', fontWeight: '700', color, lineHeight: 1 }}>{value}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '6px' }}>{title}</div>
          {subtitle && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '2px' }}>{subtitle}</div>
          )}
        </div>
        <span style={{ fontSize: '1.5rem', opacity: 0.6 }}>{icon}</span>
      </div>
    </div>
  );

  const BarChart = ({ data, title, labelKey, valueKey, color, maxItems = 10 }) => {
    if (!data || data.length === 0) return null;
    const items = data.slice(0, maxItems);
    const maxValue = Math.max(...items.map(d => d[valueKey] || 0));
    if (maxValue === 0) return null;

    return (
      <div style={{ marginBottom: '20px' }}>
        {title && <h4 style={{ marginBottom: '12px', fontSize: '0.9rem', color: 'var(--text-primary)' }}>{title}</h4>}
        {items.map((item, i) => (
          <div key={i} style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-primary)' }}>{item[labelKey] || 'Inconnu'}</span>
              <span style={{ fontWeight: '600', color }}>{item[valueKey]}</span>
            </div>
            <div style={{ height: '6px', background: 'var(--bg-primary)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                width: `${(item[valueKey] / maxValue) * 100}%`,
                height: '100%',
                background: color,
                borderRadius: '3px',
                transition: 'width 0.5s ease'
              }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const PieChart = ({ data, title, colors }) => {
    if (!data || data.length === 0) return null;
    const total = data.reduce((sum, d) => sum + d.value, 0);
    if (total === 0) return null;

    // Calculer les segments du camembert avec conic-gradient
    let gradientParts = [];
    let currentAngle = 0;
    data.forEach((d, i) => {
      const angle = (d.value / total) * 360;
      gradientParts.push(`${colors[i % colors.length]} ${currentAngle}deg ${currentAngle + angle}deg`);
      currentAngle += angle;
    });

    return (
      <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
        <h4 style={{ marginBottom: '15px', fontSize: '0.9rem', color: 'var(--text-primary)' }}>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            background: `conic-gradient(${gradientParts.join(', ')})`,
            flexShrink: 0
          }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                <span style={{
                  width: '12px', height: '12px', borderRadius: '3px',
                  background: colors[i % colors.length], display: 'inline-block', flexShrink: 0
                }} />
                <span style={{ color: 'var(--text-primary)' }}>{d.label}</span>
                <span style={{ fontWeight: '700', color: colors[i % colors.length] }}>{d.value}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  ({total > 0 ? Math.round((d.value / total) * 100) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const TimelineChart = ({ data, title, color, stackedData, stackColors }) => {
    if (!data || data.length === 0) return null;

    const isStacked = !!stackedData;
    let maxValue;
    if (isStacked) {
      maxValue = Math.max(...data.map(d => (d.odoo || 0) + (d.whatsapp || 0)));
    } else {
      maxValue = Math.max(...data.map(d => d.count || 0));
    }
    if (maxValue === 0) return null;

    const items = data.slice(-30);

    return (
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginBottom: '15px', fontSize: '0.9rem', color: 'var(--text-primary)' }}>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '140px', position: 'relative' }}>
          {items.map((item, i) => {
            if (isStacked) {
              const odooH = maxValue > 0 ? ((item.odoo || 0) / maxValue) * 100 : 0;
              const waH = maxValue > 0 ? ((item.whatsapp || 0) / maxValue) * 100 : 0;
              const total = (item.odoo || 0) + (item.whatsapp || 0);
              return (
                <div key={i}
                  style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    justifyContent: 'flex-end', height: '100%', position: 'relative',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltip({
                      x: rect.left + rect.width / 2,
                      y: rect.top - 10,
                      content: `${formatDate(item.date)}\nOdoo: ${item.odoo || 0}\nWA: ${item.whatsapp || 0}\nTotal: ${total}`
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <div style={{ width: '100%', height: `${waH}%`, background: stackColors[1], borderRadius: '2px 2px 0 0', minHeight: waH > 0 ? '2px' : '0' }} />
                  <div style={{ width: '100%', height: `${odooH}%`, background: stackColors[0], minHeight: odooH > 0 ? '2px' : '0' }} />
                </div>
              );
            }
            const h = maxValue > 0 ? ((item.count || 0) / maxValue) * 100 : 0;
            return (
              <div key={i}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  justifyContent: 'flex-end', height: '100%', cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    x: rect.left + rect.width / 2,
                    y: rect.top - 10,
                    content: `${formatDate(item.date || item.week)}\n${item.count}`
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                <div style={{ width: '100%', height: `${h}%`, background: color, borderRadius: '2px 2px 0 0', minHeight: h > 0 ? '2px' : '0' }} />
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
          <span>{formatDate(items[0]?.date)}</span>
          <span>{formatDate(items[items.length - 1]?.date)}</span>
        </div>
        {isStacked && (
          <div style={{ display: 'flex', gap: '15px', marginTop: '8px', fontSize: '0.75rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: stackColors[0], display: 'inline-block' }} /> Odoo
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: stackColors[1], display: 'inline-block' }} /> WhatsApp
            </span>
          </div>
        )}
      </div>
    );
  };

  // --- Tabs ---
  const tabs = [
    { id: 'overview', label: "Vue d'ensemble" },
    { id: 'timeline', label: 'Tendances' },
    { id: 'teams', label: 'Équipes' },
    { id: 'agents', label: 'Agents' },
    { id: 'activity', label: 'Activité' }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100%', background: 'var(--bg-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
          <div style={{ color: 'var(--text-muted)' }}>Chargement des statistiques...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', overflow: 'hidden' }}>
      {/* Tooltip global */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x,
          top: tooltip.y,
          transform: 'translate(-50%, -100%)',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '0.78rem',
          color: 'var(--text-primary)',
          whiteSpace: 'pre-line',
          zIndex: 1000,
          pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          fontWeight: '500',
          lineHeight: '1.5'
        }}>
          {tooltip.content}
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '16px 24px',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button
            onClick={onBack}
            style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '8px 14px',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ← Retour
          </button>
          <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Statistiques</h2>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: days === d ? '2px solid var(--accent)' : '1px solid var(--border)',
                  background: days === d ? 'var(--accent)' : 'var(--bg-primary)',
                  color: days === d ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: days === d ? '600' : '400'
                }}
              >
                {d}j
              </button>
            ))}
          </div>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              maxWidth: '200px'
            }}
          >
            <option value="">Toutes les équipes</option>
            {teamsList.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0',
        padding: '0 24px',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: activeTab === tab.id ? '600' : '400',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px', maxWidth: '100%' }}>
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            color: '#ef4444'
          }}>
            {error}
          </div>
        )}

        {stats && activeTab === 'overview' && (
          <>
            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
              <StatCard title="Tickets ouverts" value={stats.overview.totalOpen} subtitle={`Odoo: ${stats.overview.odooOpen} | WA: ${stats.overview.waOpen}`} color="#0d9488" icon="📋" />
              <StatCard title="Nouveaux aujourd'hui" value={stats.overview.newToday} color="#3b82f6" icon="🆕" />
              <StatCard title="Résolus aujourd'hui" value={stats.overview.resolvedToday} color="#22c55e" icon="✅" />
              <StatCard title="Temps moyen résolution" value={formatHours(stats.overview.avgResolutionHours)} subtitle={`Sur ${days} jours`} color="#f59e0b" icon="⏱️" />
              <StatCard title="Non assignés" value={stats.overview.unassigned} color="#ef4444" icon="⚠️" />
              <StatCard title="Actions aujourd'hui" value={stats.overview.actionsToday} color="#8b5cf6" icon="⚡" />
            </div>

            {/* Charts row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart data={[...stats.teams].sort((a, b) => b.open_tickets - a.open_tickets)} title="Tickets ouverts par équipe" labelKey="name" valueKey="open_tickets" color="#7c3aed" />
              </div>
              {stats.origins && (
                <PieChart
                  data={[
                    { label: 'Par email', value: stats.origins.email },
                    { label: 'Création manuelle', value: stats.origins.manual }
                  ]}
                  title={`Origine des tickets Odoo (${days}j)`}
                  colors={['#3b82f6', '#f59e0b']}
                />
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart
                  data={[...stats.agents].filter(a => a.resolved_count > 0).sort((a, b) => b.resolved_count - a.resolved_count)}
                  title="Top agents — tickets résolus"
                  labelKey="name" valueKey="resolved_count" color="#22c55e"
                />
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart data={stats.agents.filter(a => a.action_count > 0)} title="Top agents — actions" labelKey="name" valueKey="action_count" color="#0d9488" />
              </div>
            </div>
          </>
        )}

        {stats && activeTab === 'timeline' && (
          <>
            <TimelineChart
              data={stats.timeline.ticketsCreatedByDay}
              title={`Tickets créés par jour (${days}j)`}
              stackedData
              stackColors={['#7c3aed', '#25d366']}
            />

            <TimelineChart
              data={stats.timeline.ticketsResolvedByDay}
              title={`Tickets résolus par jour (${days}j)`}
              stackedData
              stackColors={['#7c3aed', '#25d366']}
            />

            {/* Répartition par type de résolution */}
            {stats.resolutionTypes && stats.resolutionTypes.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                <PieChart
                  data={stats.resolutionTypes.map(r => ({ label: r.name, value: r.count }))}
                  title={`Types de résolution (${days}j)`}
                  colors={['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#0d9488']}
                />
                <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                  <h4 style={{ marginBottom: '15px', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                    Détail des résolutions ({days}j)
                  </h4>
                  {stats.resolutionTypes.map((item, i) => (
                    <div key={i} style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '0.8rem' }}>
                        <span style={{ color: 'var(--text-primary)' }}>{item.name}</span>
                        <span style={{ fontWeight: '600', color: '#22c55e' }}>{item.count}</span>
                      </div>
                      <div style={{ height: '6px', background: 'var(--bg-primary)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${(item.count / Math.max(...stats.resolutionTypes.map(r => r.count))) * 100}%`,
                          height: '100%',
                          background: '#22c55e',
                          borderRadius: '3px',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <TimelineChart
              data={stats.timeline.actionsByDay}
              title={`Actions agents par jour (${days}j)`}
              color="#8b5cf6"
            />

            {stats.timeline.avgResolutionByWeek && stats.timeline.avgResolutionByWeek.length > 0 && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ marginBottom: '15px', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                  Temps moyen de résolution par semaine
                </h4>
                {stats.timeline.avgResolutionByWeek.map((item, i) => (
                  <div key={i} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', fontSize: '0.8rem' }}>
                      <span>Semaine {item.week}</span>
                      <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                        {formatHours(item.avg_hours)} ({item.count} tickets)
                      </span>
                    </div>
                    <div style={{ height: '6px', background: 'var(--bg-primary)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min((item.avg_hours / Math.max(...stats.timeline.avgResolutionByWeek.map(w => w.avg_hours))) * 100, 100)}%`,
                        height: '100%',
                        background: '#f59e0b',
                        borderRadius: '3px'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {stats && activeTab === 'teams' && (
          <>
            {stats.teams.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>
                Aucune donnée d'équipe disponible
              </div>
            ) : (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={thStyle}>Équipe</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Ouverts</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Résolus ({days}j)</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Temps moyen</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.teams.map((team, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: '600' }}>{team.name}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={{
                            background: team.open_tickets > 10 ? '#ef444420' : team.open_tickets > 5 ? '#f59e0b20' : '#22c55e20',
                            color: team.open_tickets > 10 ? '#ef4444' : team.open_tickets > 5 ? '#f59e0b' : '#22c55e',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontWeight: '600',
                            fontSize: '0.85rem'
                          }}>
                            {team.open_tickets}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={{ fontWeight: '600', color: '#22c55e' }}>
                            {team.resolved_period || 0}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#f59e0b' }}>
                          {formatHours(team.avg_resolution_hours)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: team.source === 'odoo' ? '#7c3aed20' : team.source === 'whatsapp' ? '#25d36620' : '#0d948820',
                            color: team.source === 'odoo' ? '#7c3aed' : team.source === 'whatsapp' ? '#25d366' : '#0d9488'
                          }}>
                            {team.source === 'both' ? 'Odoo+WA' : team.source === 'odoo' ? 'Odoo' : 'WA'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {stats && activeTab === 'agents' && (
          <>
            {stats.agents.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>
                Aucune donnée d'agent disponible
              </div>
            ) : (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      <th style={thStyle}>Agent</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Tickets assignés</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Résolus ({days}j)</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Actions ({days}j)</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Messages</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.agents.map((agent, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: '600' }}>{agent.name}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={{
                            background: '#0d948820',
                            color: '#0d9488',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontWeight: '600',
                            fontSize: '0.85rem'
                          }}>
                            {agent.assigned_tickets}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={{ fontWeight: '600', color: '#22c55e' }}>
                            {agent.resolved_count || 0}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600' }}>{agent.action_count}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{agent.messages_sent}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{agent.notes_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bar charts visuels */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' }}>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart
                  data={[...stats.agents].filter(a => a.resolved_count > 0).sort((a, b) => b.resolved_count - a.resolved_count)}
                  title="Tickets résolus par agent"
                  labelKey="name" valueKey="resolved_count" color="#22c55e"
                />
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart data={stats.agents.filter(a => a.assigned_tickets > 0)} title="Tickets assignés par agent" labelKey="name" valueKey="assigned_tickets" color="#0d9488" />
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart data={stats.agents.filter(a => a.action_count > 0)} title="Actions par agent" labelKey="name" valueKey="action_count" color="#8b5cf6" />
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px' }}>
                <BarChart data={stats.agents.filter(a => a.messages_sent > 0)} title="Messages envoyés par agent" labelKey="name" valueKey="messages_sent" color="#3b82f6" />
              </div>
            </div>
          </>
        )}

        {stats && activeTab === 'activity' && (
          <>
            {/* Actions par type */}
            {stats.actionsByType && stats.actionsByType.length > 0 && (
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '15px', fontSize: '0.9rem' }}>Répartition des actions ({days}j)</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {stats.actionsByType.map((a, i) => (
                    <div key={i} style={{
                      padding: '6px 12px',
                      background: 'var(--bg-primary)',
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{ color: 'var(--text-muted)' }}>{actionTypeLabels[a.action_type] || a.action_type}</span>
                      <span style={{ fontWeight: '700', color: 'var(--accent)' }}>{a.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Journal des dernieres actions */}
            <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Dernières actions</h4>
              </div>
              <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                {stats.recentActions && stats.recentActions.length > 0 ? (
                  stats.recentActions.map((action, i) => (
                    <div key={i} style={{
                      padding: '10px 20px',
                      borderBottom: '1px solid var(--border)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '0.85rem',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', minWidth: '100px' }}>
                        {formatDateTime(action.created_at)}
                      </span>
                      <span style={{
                        fontWeight: '600',
                        color: 'var(--accent)',
                        minWidth: '120px'
                      }}>
                        {action.agent_name || 'Système'}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        background: 'var(--bg-primary)',
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        minWidth: '120px',
                        textAlign: 'center'
                      }}>
                        {actionTypeLabels[action.action_type] || action.action_type}
                      </span>
                      <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: '100px' }}>
                        {action.details || '-'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Aucune action enregistrée
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Styles tableaux
const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '0.8rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  fontWeight: '600',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '12px 16px',
  fontSize: '0.9rem',
  color: 'var(--text-primary)'
};

export default StatisticsView;
